import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Plus, Search, ArrowLeft, Send, LifeBuoy, X, Package, UserCheck, Download } from 'lucide-react';
import { api, downloadBlob } from '@/api/client';
import { can, useAuth } from '@/hooks/useAuth';
import { SearchableSelect } from '@/components/ui/SearchableSelect';

type Cat = { value: string; label: string };
type Meta = { categories: Cat[]; priorities: string[]; statuses: string[] };
type Msg = { id: number; authorType: string; authorName?: string; body: string; isInternalNote: boolean; createdAt: string; attachmentUrl?: string | null };

/** Read a File as a data URL (capped ~3MB to keep DB rows sane). */
function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (file.size > 3 * 1024 * 1024) { reject(new Error('File too large (max 3 MB).')); return; }
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error('Could not read file.'));
    r.readAsDataURL(file);
  });
}
type Ticket = {
  id: number; ticketNo: string; merchantId?: number | null; merchantName?: string | null;
  subject: string; category: string; priority: string; status: string;
  /** PUBLIC = merchant + internal; PRIVATE = internal/admin only. Merchants are never sent PRIVATE rows. */
  visibility?: 'PUBLIC' | 'PRIVATE';
  orderType?: 'B2C' | 'B2B' | null; relatedOrderId?: string | null; relatedAwb?: string | null; relatedLr?: string | null; relatedRef?: string | null;
  createdByType: string; createdByName?: string | null; assignedToId?: number | null; assignedToName?: string | null;
  lastReplyAt?: string | null; createdAt: string; updatedAt: string; messages?: Msg[];
  ageHours?: number; slaHours?: number; awaitingFirstResponse?: boolean; firstResponseHours?: number | null; slaBreached?: boolean;
};

const STATUS_BADGE: Record<string, string> = {
  OPEN: 'bg-blue-100 text-blue-700', IN_PROGRESS: 'bg-amber-100 text-amber-700',
  WAITING_MERCHANT: 'bg-violet-100 text-violet-700', RESOLVED: 'bg-emerald-100 text-emerald-700', CLOSED: 'bg-slate-200 text-slate-600',
};
const PRIO_BADGE: Record<string, string> = {
  LOW: 'bg-slate-100 text-slate-600', MEDIUM: 'bg-sky-100 text-sky-700', HIGH: 'bg-orange-100 text-orange-700', URGENT: 'bg-rose-100 text-rose-700',
};
const statusLabel = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const fmt = (s?: string | null) => (s ? new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

export function TicketsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isMerchant = user?.kind === 'merchant';
  // Same permission the single-ticket assign dropdown needs — the bulk bar must not appear for
  // someone who would be refused one ticket at a time.
  const canAssign = !isMerchant && can(user, 'Tickets', 'All', 'edit');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [checked, setChecked] = useState<number[]>([]);
  const [bulkUser, setBulkUser] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [mine, setMine] = useState(false);
  const [visibility, setVisibility] = useState('');

  const { data: meta } = useQuery<Meta>({ queryKey: ['ticket-meta'], queryFn: async () => (await api.get('/tickets/meta')).data });
  const { data: stats } = useQuery<any>({ queryKey: ['ticket-stats'], queryFn: async () => (await api.get('/tickets/stats')).data });
  const { data, isLoading } = useQuery<{ items: Ticket[]; total: number }>({
    queryKey: ['tickets', { status, category, priority, q, mine, visibility }],
    queryFn: async () => {
      const params: any = {};
      if (status) params.status = status; if (category) params.category = category; if (priority) params.priority = priority; if (q) params.q = q;
      if (mine && !isMerchant && user?.id) params.assignedToId = user.id;
      if (visibility && !isMerchant) params.visibility = visibility;
      return (await api.get('/tickets', { params })).data;
    },
  });
  const catLabel = useMemo(() => Object.fromEntries((meta?.categories ?? []).map((c) => [c.value, c.label])), [meta]);

  // Who this user may hand a ticket to. Scoped server-side to their team, so a CS Manager sees their
  // agents and an agent sees only themselves — the picker is no longer a list of the whole company.
  // Shares its cache key with the one inside TicketDetail, so this is not a second request.
  const { data: assignableData } = useQuery<{ items: { id: number; name: string; userType?: string; isMe?: boolean }[]; hasTeam: boolean; isAdmin: boolean }>({
    queryKey: ['ticket-assignable'], enabled: !isMerchant,
    queryFn: async () => (await api.get('/tickets/assignable')).data,
  });
  const teamOptions = useMemo(
    () => (assignableData?.items ?? []).map((u) => ({ value: u.id, label: u.isMe ? `${u.name} (me)` : u.name, hint: u.userType?.replace(/_/g, ' ') })),
    [assignableData],
  );

  const [exporting, setExporting] = useState(false);
  /**
   * Download the queue as it is on screen. `scope` narrows it further without touching the filters:
   * `mine` is the agent's own tickets, `open` is everything still live in whatever they can see.
   * The server applies the same visibility rule as the list, so this can never export more than the
   * screen already shows.
   */
  const runExport = async (scope?: 'mine' | 'open') => {
    setExporting(true);
    try {
      const params: any = {};
      if (status) params.status = status;
      if (category) params.category = category;
      if (priority) params.priority = priority;
      if (q) params.q = q;
      if (visibility && !isMerchant) params.visibility = visibility;
      if (mine && !isMerchant && user?.id) params.assignedToId = user.id;
      if (scope === 'mine') { params.mine = '1'; params.open = '1'; delete params.status; }
      if (scope === 'open') { params.open = '1'; delete params.status; }
      const stamp = new Date().toLocaleDateString('en-CA');
      const tag = scope === 'mine' ? 'my-open' : scope === 'open' ? 'open' : 'filtered';
      await downloadBlob('/tickets/export', `tickets-${tag}-${stamp}.csv`, { params });
      toast.success('Export downloaded');
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const items = data?.items ?? [];
  const pageIds = items.map((t) => t.id);
  // A tick is a promise about a specific row. Changing the filter changes which rows exist, so the
  // selection is dropped rather than carried over onto a list the operator never looked at.
  useEffect(() => { setChecked([]); }, [status, category, priority, q, mine, visibility]);

  const bulkAssign = useMutation({
    mutationFn: async () => (await api.post('/tickets/bulk-assign', { ids: checked, assignedToId: bulkUser })).data,
    onSuccess: (r: any) => {
      toast.success(
        r.assigned === 0
          ? 'Those tickets were already assigned to that user.'
          : `${r.assigned} ticket(s) assigned to ${r.assignedToName ?? 'nobody'}${r.skipped ? ` · ${r.skipped} already there` : ''}`,
      );
      setChecked([]); setBulkUser(null);
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['ticket-stats'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Bulk assign failed'),
  });

  if (selectedId) return <TicketDetail id={selectedId} isMerchant={isMerchant} meta={meta} onBack={() => setSelectedId(null)} />;

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><LifeBuoy size={22} className="text-primary" /> Support Tickets</h1>
          <p className="text-sm text-slate-500">{isMerchant ? 'Raise and track support requests with our team.' : 'Merchant support tickets — NDR, weight, COD, pickup, billing and more.'}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* One click for "everything I can see that is still open" — the CS Manager's whole team,
              or one agent's own queue, decided by the server rather than by which button was used. */}
          <button onClick={() => runExport('open')} disabled={exporting}
            className="px-3 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-50"
            title="Download every OPEN ticket you can see">
            {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Export Open
          </button>
          {!isMerchant && (
            <button onClick={() => runExport('mine')} disabled={exporting}
              className="px-3 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-50"
              title="Download only the open tickets assigned to you">
              <Download size={15} /> My Open
            </button>
          )}
          <button onClick={() => runExport()} disabled={exporting}
            className="px-3 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-50"
            title="Download exactly what the current filters show">
            <Download size={15} /> Export Filtered
          </button>
          <button onClick={() => setCreateOpen(true)} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-primary/90"><Plus size={15} /> New Ticket</button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {[['Open', stats?.open, 'text-blue-700'], ['In Progress', stats?.inProgress, 'text-amber-700'], ['Waiting', stats?.waiting, 'text-violet-700'], ['Resolved', stats?.resolved, 'text-emerald-700']].map(([l, v, t]) => (
          <div key={l as string} className="bg-white border border-slate-200 rounded-xl px-3 py-2.5">
            <div className="text-[11px] text-slate-500">{l as string}</div>
            <div className={`text-lg font-bold ${t as string}`}>{(v as number) ?? 0}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') setQ(search.trim()); }}
            placeholder="Search ticket no / subject / AWB / LR" className="input w-full pl-9" />
        </div>
        <button onClick={() => setQ(search.trim())} className="px-3 py-2 bg-primary text-white rounded-lg text-sm">Search</button>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border rounded-lg px-2 py-2 text-sm">
          <option value="">All statuses</option>
          {(meta?.statuses ?? []).map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="border rounded-lg px-2 py-2 text-sm">
          <option value="">All categories</option>
          {(meta?.categories ?? []).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select value={priority} onChange={(e) => setPriority(e.target.value)} className="border rounded-lg px-2 py-2 text-sm">
          <option value="">Any priority</option>
          {(meta?.priorities ?? []).map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        {!isMerchant && (
          <select value={visibility} onChange={(e) => setVisibility(e.target.value)} className="border rounded-lg px-2 py-2 text-sm">
            <option value="">Public + Private</option>
            <option value="PUBLIC">Public only</option>
            <option value="PRIVATE">Private only</option>
          </select>
        )}
        {!isMerchant && (
          <button onClick={() => setMine((v) => !v)}
            className={`px-3 py-2 rounded-lg text-sm border ${mine ? 'bg-primary text-white border-primary' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
            Assigned to me
          </button>
        )}
      </div>

      {/* Bulk bar — only while something is ticked, so the ordinary list stays uncluttered. */}
      {canAssign && checked.length > 0 && (
        <div className="bg-primary/5 border border-primary/30 rounded-xl p-3 mb-3 flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
            <UserCheck size={15} className="text-primary" /> {checked.length} ticket{checked.length > 1 ? 's' : ''} selected
          </span>
          <div className="min-w-[260px]">
            {/* Scoped to the caller's team — the old picker listed every active user in the company,
                so a ticket could be parked on someone who would never look at it. The server refuses
                an out-of-team assignment too; this just stops it being offered. */}
            <SearchableSelect
              value={bulkUser ?? ''}
              onChange={(v) => setBulkUser(v === '' || v == null ? null : Number(v))}
              options={teamOptions}
              placeholder={teamOptions.length ? 'Assign to…' : 'Nobody reports to you yet'}
            />
          </div>
          <button
            onClick={() => bulkAssign.mutate()}
            disabled={!bulkUser || bulkAssign.isPending}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1.5 disabled:opacity-50">
            {bulkAssign.isPending ? <Loader2 size={14} className="animate-spin" /> : <UserCheck size={14} />} Assign
          </button>
          {/* Unassign is deliberately its own button rather than a blank entry in the dropdown — an
              empty picker is what you see before choosing, so it must not also mean "clear owner". */}
          <button
            onClick={() => { if (confirm(`Send ${checked.length} ticket(s) back to the unassigned pool?`)) { setBulkUser(null); bulkAssign.mutate(); } }}
            disabled={bulkAssign.isPending}
            className="px-3 py-2 border border-slate-300 text-slate-600 rounded-lg text-sm hover:bg-white disabled:opacity-50">
            Unassign
          </button>
          <button onClick={() => setChecked([])} className="text-sm text-slate-500 hover:text-slate-800 underline ml-auto">Clear selection</button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              {canAssign && (
                <th className="px-3 py-2.5 w-9">
                  <input
                    type="checkbox"
                    className="accent-primary"
                    aria-label="Select all tickets on this page"
                    checked={pageIds.length > 0 && pageIds.every((id) => checked.includes(id))}
                    ref={(el) => { if (el) el.indeterminate = checked.length > 0 && !pageIds.every((id) => checked.includes(id)); }}
                    onChange={(e) => setChecked(e.target.checked ? Array.from(new Set([...checked, ...pageIds])) : checked.filter((id) => !pageIds.includes(id)))}
                  />
                </th>
              )}
              <th className="text-left px-3 py-2.5">Ticket</th>
              <th className="text-left px-3 py-2.5">Subject</th>
              <th className="text-left px-3 py-2.5">Category</th>
              {!isMerchant && <th className="text-left px-3 py-2.5">Merchant</th>}
              <th className="text-left px-3 py-2.5">Priority</th>
              <th className="text-left px-3 py-2.5">Status</th>
              {!isMerchant && <th className="text-left px-3 py-2.5">Assigned</th>}
              <th className="text-left px-3 py-2.5">Updated</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (<tr><td colSpan={9} className="text-center py-10"><Loader2 size={20} className="mx-auto animate-spin text-primary" /></td></tr>)}
            {!isLoading && items.length === 0 && (<tr><td colSpan={9} className="text-center py-12 text-slate-400">No tickets.</td></tr>)}
            {items.map((t) => (
              <tr key={t.id} onClick={() => setSelectedId(t.id)}
                className={`border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer ${checked.includes(t.id) ? 'bg-primary/5' : ''}`}>
                {canAssign && (
                  // The row opens the ticket, so the tick must not bubble — otherwise every click
                  // that selects a ticket also navigates away from the list you are selecting on.
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="accent-primary"
                      aria-label={`Select ticket ${t.ticketNo}`}
                      checked={checked.includes(t.id)}
                      onChange={(e) => setChecked(e.target.checked ? [...checked, t.id] : checked.filter((id) => id !== t.id))}
                    />
                  </td>
                )}
                <td className="px-3 py-2 font-mono text-xs font-medium text-primary">{t.ticketNo}</td>
                <td className="px-3 py-2">
                  <div className="max-w-[280px] truncate flex items-center gap-1.5">
                    {/* Only staff ever see this — a merchant is never served a private ticket at all. */}
                    {!isMerchant && t.visibility === 'PRIVATE' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-white shrink-0" title="Internal only — the merchant cannot see this ticket">
                        PRIVATE
                      </span>
                    )}
                    <span className="truncate">{t.subject}</span>
                  </div>
                  {(t.relatedAwb || t.relatedLr) && <div className="text-[11px] text-slate-400 font-mono">{t.relatedAwb || t.relatedLr}</div>}
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">{catLabel[t.category] ?? t.category}</td>
                {!isMerchant && <td className="px-3 py-2 text-xs text-slate-600">{t.merchantName || '—'}</td>}
                <td className="px-3 py-2"><span className={`text-[11px] px-2 py-0.5 rounded ${PRIO_BADGE[t.priority] ?? ''}`}>{t.priority}</span></td>
                <td className="px-3 py-2">
                  <span className={`text-[11px] px-2 py-0.5 rounded ${STATUS_BADGE[t.status] ?? ''}`}>{statusLabel(t.status)}</span>
                  {t.slaBreached ? <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700" title={`No first response in ${t.slaHours}h`}>SLA overdue</span>
                    : t.awaitingFirstResponse ? <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Awaiting reply</span> : null}
                </td>
                {!isMerchant && <td className="px-3 py-2 text-xs text-slate-600">{t.assignedToName || <span className="text-slate-400">Unassigned</span>}</td>}
                <td className="px-3 py-2 text-xs text-slate-500">{fmt(t.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {createOpen && <CreateTicketModal isMerchant={isMerchant} meta={meta} onClose={() => setCreateOpen(false)} onDone={(id) => { setCreateOpen(false); setSelectedId(id); }} />}
    </div>
  );
}

function TicketDetail({ id, isMerchant, meta, onBack }: { id: number; isMerchant: boolean; meta?: Meta; onBack: () => void }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [reply, setReply] = useState('');
  const [internalNote, setInternalNote] = useState(false);
  const [replyFile, setReplyFile] = useState<{ url: string; name: string } | null>(null);
  const { data: t, isLoading } = useQuery<Ticket>({ queryKey: ['ticket', id], queryFn: async () => (await api.get(`/tickets/${id}`)).data });
  // The server scopes this to the caller's team (themselves + anyone reporting to them); an admin
  // gets everyone. `hasTeam` is what tells a manager apart from an agent with nobody under them.
  const { data: assignableData } = useQuery<{ items: { id: number; name: string; isMe?: boolean }[]; hasTeam: boolean; isAdmin: boolean }>({
    queryKey: ['ticket-assignable'], enabled: !isMerchant,
    queryFn: async () => (await api.get('/tickets/assignable')).data,
  });
  // The dropdown only offers CS and managers. A ticket assigned BEFORE that narrowing — or moved by
  // an admin to someone outside it — still has to show its real owner: a <select> whose value has no
  // matching <option> falls back to the first one, so the box would silently read "Unassigned" on a
  // ticket that is very much assigned, and the next save would actually unassign it.
  const assignable = useMemo(() => {
    const items = assignableData?.items ?? [];
    if (!t?.assignedToId || items.some((u) => u.id === t.assignedToId)) return items;
    return [...items, { id: t.assignedToId, name: t.assignedToName || `User #${t.assignedToId}` }];
  }, [assignableData, t?.assignedToId, t?.assignedToName]);

  const invalidate = () => { qc.invalidateQueries({ queryKey: ['ticket', id] }); qc.invalidateQueries({ queryKey: ['tickets'] }); qc.invalidateQueries({ queryKey: ['ticket-stats'] }); };
  const sendReply = useMutation({
    mutationFn: async () => (await api.post(`/tickets/${id}/reply`, { body: reply.trim(), isInternalNote: internalNote, attachmentUrl: replyFile?.url })).data,
    onSuccess: () => { setReply(''); setInternalNote(false); setReplyFile(null); invalidate(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to send'),
  });
  const patch = useMutation({
    mutationFn: async (body: any) => (await api.patch(`/tickets/${id}`, body)).data,
    onSuccess: () => { toast.success('Ticket updated'); invalidate(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Update failed'),
  });

  if (isLoading || !t) return <div className="flex justify-center py-20"><Loader2 size={22} className="animate-spin text-primary" /></div>;
  const catLabel = Object.fromEntries((meta?.categories ?? []).map((c) => [c.value, c.label]));

  return (
    <div className="p-6 max-w-[1200px] mx-auto">
      <button onClick={onBack} className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1 mb-3"><ArrowLeft size={15} /> Back to tickets</button>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm text-primary font-semibold">{t.ticketNo}</span>
            <span className={`text-[11px] px-2 py-0.5 rounded ${STATUS_BADGE[t.status] ?? ''}`}>{statusLabel(t.status)}</span>
            <span className={`text-[11px] px-2 py-0.5 rounded ${PRIO_BADGE[t.priority] ?? ''}`}>{t.priority}</span>
            {!isMerchant && t.visibility === 'PRIVATE' && (
              <span className="text-[11px] px-2 py-0.5 rounded bg-slate-800 text-white" title="Internal only — the merchant cannot see this ticket or its emails">PRIVATE</span>
            )}
          </div>
          <h1 className="text-xl font-bold text-slate-900 mt-1">{t.subject}</h1>
          <div className="text-xs text-slate-500 mt-1">
            {catLabel[t.category] ?? t.category}{!isMerchant && t.merchantName ? ` · ${t.merchantName}` : ''} · opened by {t.createdByName} · {fmt(t.createdAt)}
          </div>
          {(t.relatedAwb || t.relatedLr || t.relatedRef) && (
            <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Package size={12} /> {[t.relatedAwb, t.relatedLr, t.relatedRef].filter(Boolean).join(' · ')}</div>
          )}
          <div className="text-xs mt-1">
            {t.slaBreached ? <span className="text-rose-600 font-medium">⚠ SLA overdue — no first response in {t.slaHours}h (open {t.ageHours}h)</span>
              : t.awaitingFirstResponse ? <span className="text-amber-600">Awaiting first response · SLA {t.slaHours}h · open {t.ageHours}h</span>
              : t.firstResponseHours != null ? <span className="text-slate-400">First response in {t.firstResponseHours}h · open {t.ageHours}h</span>
              : <span className="text-slate-400">Open {t.ageHours}h</span>}
          </div>
        </div>
        {!isMerchant && (
          <div className="flex flex-wrap gap-2">
            <select value={t.status} onChange={(e) => patch.mutate({ status: e.target.value })} className="border rounded-lg px-2 py-1.5 text-sm">
              {(meta?.statuses ?? []).map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
            </select>
            <select value={t.priority} onChange={(e) => patch.mutate({ priority: e.target.value })} className="border rounded-lg px-2 py-1.5 text-sm">
              {(meta?.priorities ?? []).map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <select value={t.assignedToId ?? ''} onChange={(e) => patch.mutate({ assignedToId: e.target.value || null })} className="border rounded-lg px-2 py-1.5 text-sm">
              <option value="">Unassigned</option>
              {assignable.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            {user?.id && t.assignedToId !== user.id && (
              <button onClick={() => patch.mutate({ assignedToId: user.id })} className="px-3 py-1.5 border border-primary text-primary rounded-lg text-sm hover:bg-primary/5">Assign to me</button>
            )}
            {/* Confirmed, not a silent toggle: switching to Public exposes every reply already on
                this thread to the merchant, and switching to Private takes their own ticket away. */}
            <button
              onClick={() => {
                const goingPublic = t.visibility === 'PRIVATE';
                const msg = goingPublic
                  ? 'Make this ticket PUBLIC?\n\nThe merchant will be able to read this whole thread, including replies already written on it (internal notes stay hidden).'
                  : 'Make this ticket PRIVATE?\n\nThe merchant will no longer see this ticket at all, and will stop receiving emails about it.';
                if (confirm(msg)) patch.mutate({ visibility: goingPublic ? 'PUBLIC' : 'PRIVATE' });
              }}
              className={`px-3 py-1.5 rounded-lg text-sm border ${
                t.visibility === 'PRIVATE'
                  ? 'border-slate-800 text-slate-800 hover:bg-slate-100'
                  : 'border-slate-300 text-slate-600 hover:bg-slate-50'
              }`}>
              {t.visibility === 'PRIVATE' ? 'Make public' : 'Make private'}
            </button>
          </div>
        )}
      </div>

      <div className="space-y-3 mb-4">
        {(t.messages ?? []).map((m) => {
          const mine = isMerchant ? m.authorType === 'MERCHANT' : m.authorType === 'INTERNAL';
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] rounded-xl px-4 py-2.5 text-sm ${m.isInternalNote ? 'bg-amber-50 border border-amber-200' : mine ? 'bg-primary/10 border border-primary/20' : 'bg-white border border-slate-200'}`}>
                <div className="text-[11px] text-slate-500 mb-0.5 flex items-center gap-1.5">
                  <span className="font-medium text-slate-700">{m.authorName || m.authorType}</span>
                  {m.isInternalNote && <span className="bg-amber-200 text-amber-800 rounded px-1.5 text-[10px]">Internal note</span>}
                  <span>· {fmt(m.createdAt)}</span>
                </div>
                <div className="whitespace-pre-wrap text-slate-800">{m.body}</div>
                {m.attachmentUrl && <a href={m.attachmentUrl} download target="_blank" rel="noreferrer" className="text-xs text-primary underline mt-1 inline-block">📎 Attachment</a>}
              </div>
            </div>
          );
        })}
      </div>

      {t.status === 'CLOSED' ? (
        <div className="text-center text-sm text-slate-400 py-4 border-t">This ticket is closed.{!isMerchant && ' Change status above to reopen.'}</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl p-3">
          <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3} placeholder="Write a reply…" className="w-full border rounded-lg px-3 py-2 text-sm" />
          <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
            <div className="flex items-center gap-3">
              {!isMerchant && (
                <label className="text-xs text-slate-500 flex items-center gap-1.5">
                  <input type="checkbox" checked={internalNote} onChange={(e) => setInternalNote(e.target.checked)} className="accent-amber-500" /> Internal note (hidden from merchant)
                </label>
              )}
              <label className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                📎 Attach
                <input type="file" className="hidden" onChange={(e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  readFileAsDataUrl(f).then((url) => setReplyFile({ url, name: f.name })).catch((err) => toast.error(err.message));
                }} />
              </label>
              {replyFile && <span className="text-xs text-slate-600">{replyFile.name} <button onClick={() => setReplyFile(null)} className="text-rose-500">✕</button></span>}
            </div>
            <button onClick={() => sendReply.mutate()} disabled={(!reply.trim() && !replyFile) || sendReply.isPending}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-50">
              {sendReply.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {internalNote ? 'Add note' : 'Send reply'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateTicketModal({ isMerchant, meta, onClose, onDone }: { isMerchant: boolean; meta?: Meta; onClose: () => void; onDone: (id: number) => void }) {
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('NDR');
  const [priority, setPriority] = useState('MEDIUM');
  const [body, setBody] = useState('');
  const [orderRef, setOrderRef] = useState('');
  const [orderType, setOrderType] = useState('');
  const [merchantId, setMerchantId] = useState<number | ''>('');
  const [attach, setAttach] = useState<{ url: string; name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Merchants never choose — their own ticket is always PUBLIC, and the API ignores anything else.
  const [visibility, setVisibility] = useState<'PUBLIC' | 'PRIVATE'>('PUBLIC');

  const { data: merchants } = useQuery({ queryKey: ['merchants-light-tkt'], enabled: !isMerchant, queryFn: async () => (await api.get('/merchants', { params: { pageSize: 500 } })).data });
  const merchantList = useMemo(() => (Array.isArray(merchants) ? merchants : (merchants as any)?.items ?? []), [merchants]);

  const save = useMutation({
    mutationFn: async () => (await api.post('/tickets', {
      subject: subject.trim(), category, priority, body: body.trim(),
      orderRef: orderRef.trim() || undefined, orderType: orderType || undefined,
      attachmentUrl: attach?.url,
      merchantId: isMerchant ? undefined : (merchantId || undefined),
      visibility: isMerchant ? undefined : visibility,
    })).data,
    onSuccess: (t: any) => { toast.success(`Ticket ${t.ticketNo} created`); onDone(t.id); },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Failed to create ticket'),
  });
  // Mirrors SHIPMENT_CATEGORIES on the API. Kept in step by the error the server returns if it ever
  // drifts — the rule is enforced there, this only stops the user reaching a refusal.
  const SHIPMENT_CATEGORIES = ['NDR', 'WEIGHT_DISPUTE', 'REMITTANCE_COD', 'PICKUP', 'DELIVERY_DELAY', 'TRACKING', 'LOST_DAMAGED'];
  const needsShipment = SHIPMENT_CATEGORIES.includes(category);
  const canSave = !!subject.trim() && !!body.trim() && (isMerchant || !!merchantId) && (!needsShipment || !!orderRef.trim());

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-16 z-50 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-lg w-[560px] max-w-[95vw] my-4" onClick={(e) => e.stopPropagation()}>
        <div className="bg-primary text-white px-5 py-3 flex items-center justify-between rounded-t-lg">
          <div className="font-semibold">New Support Ticket</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded px-3 py-2">{error}</div>}
          {!isMerchant && (
            <div>
              <label className="text-xs text-gray-500 block mb-1">Merchant *</label>
              <SearchableSelect value={merchantId} onChange={(v) => setMerchantId(v ? Number(v) : '')}
                options={merchantList.map((m: any) => ({ value: m.id, label: `#${m.id} · ${m.companyName}` }))} placeholder="Select merchant" />
            </div>
          )}
          {!isMerchant && (
            <div>
              <label className="text-xs text-gray-500 block mb-1">Who can see this ticket</label>
              <div className="grid grid-cols-2 gap-2">
                {([
                  { v: 'PUBLIC' as const, title: 'Public', hint: 'Merchant + internal team. The merchant sees the thread and is emailed on replies.' },
                  { v: 'PRIVATE' as const, title: 'Private', hint: 'Internal staff and admins only. The merchant is never shown it and never emailed.' },
                ]).map((o) => (
                  <button key={o.v} type="button" onClick={() => setVisibility(o.v)}
                    className={`text-left px-3 py-2 rounded-lg border text-sm transition ${
                      visibility === o.v ? 'border-primary bg-primary/5' : 'border-slate-200 hover:bg-slate-50'
                    }`}>
                    <div className={`font-medium ${visibility === o.v ? 'text-primary' : 'text-slate-700'}`}>{o.title}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">{o.hint}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="text-xs text-gray-500 block mb-1">Subject *</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Short summary of the issue" className="w-full border rounded px-2 py-1.5 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Category *</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">
                {(meta?.categories ?? []).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Priority</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">
                {(meta?.priorities ?? []).map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">
                Related AWB / LR / Order No {needsShipment ? <span className="text-rose-600">*</span> : <span className="text-gray-400">(optional)</span>}
              </label>
              <input value={orderRef} onChange={(e) => setOrderRef(e.target.value)} placeholder="e.g. 311988299"
                className={`w-full border rounded px-2 py-1.5 text-sm ${needsShipment && !orderRef.trim() ? 'border-rose-300 bg-rose-50/40' : ''}`} />
              {needsShipment && (
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Required for this category — we cannot look into a shipment issue without the number.
                </div>
              )}
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Service (optional)</label>
              <select value={orderType} onChange={(e) => setOrderType(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm">
                <option value="">—</option><option value="B2C">B2C</option><option value="B2B">B2B</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Describe the issue *</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} className="w-full border rounded px-2 py-1.5 text-sm" />
          </div>
          <div className="flex items-center gap-2 text-xs">
            <label className="text-slate-500 cursor-pointer hover:text-slate-700">📎 Attach a file
              <input type="file" className="hidden" onChange={(e) => {
                const f = e.target.files?.[0]; if (!f) return;
                readFileAsDataUrl(f).then((url) => setAttach({ url, name: f.name })).catch((err) => setError(err.message));
              }} />
            </label>
            {attach && <span className="text-slate-600">{attach.name} <button onClick={() => setAttach(null)} className="text-rose-500">✕</button></span>}
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border rounded text-sm">Cancel</button>
          <button onClick={() => save.mutate()} disabled={!canSave || save.isPending} className="px-4 py-2 bg-primary text-white rounded text-sm disabled:opacity-50">
            {save.isPending ? 'Creating…' : 'Create Ticket'}
          </button>
        </div>
      </div>
    </div>
  );
}
